/**
 * tokenRefresh.js
 * Single shared refresh state — prevents duplicate refresh calls when
 * multiple requests get 401 simultaneously across apiClient.js and api.js.
 */

import { getRefreshToken, storeAuthData, clearAuthData } from './auth';
import { RBAC_BASE_URL } from '../config/api';

let isRefreshing = false;
let refreshQueue = [];

function processQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
}

async function doRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');

  const res = await fetch(`${RBAC_BASE_URL}/api/auth/refresh-token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ refreshToken }),
  });

  if (!res.ok) throw new Error('Refresh failed');

  const json = await res.json();
  const { accessToken, refreshToken: newRefreshToken, user } = json.data;
  storeAuthData({ accessToken, refreshToken: newRefreshToken, user });
  return accessToken;
}

/**
 * handleUnauthorized
 * Call this when a request gets a 401. Ensures only one refresh happens
 * at a time — all other callers queue up and get the same new token.
 * Returns the new access token, or throws (and redirects to /login) on failure.
 */
export async function handleUnauthorized() {
  if (isRefreshing) {
    // Another refresh already in flight — wait for it
    return new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const newToken = await doRefresh();
    processQueue(null, newToken);
    return newToken;
  } catch (err) {
    processQueue(err);
    clearAuthData();
    window.location.href = '/login';
    throw err;
  } finally {
    isRefreshing = false;
  }
}
